<template>
  <div>
    <b-card class="planner-list-card">
      <div class="gradient-line-wrapper"></div>

      <div
        class="table-header d-flex flex-wrap justify-content-between align-items-center mb-2"
      >
        <div>
          <h5 class="mb-0 resource-title">Exit Interviews</h5>
          <small class="text-muted">Staff exit interview records and attachments</small>
        </div>
      </div>

      <div
        class="pagination-controls-top d-flex justify-content-between align-items-center mb-2"
      >
        <div class="page-size-wrapper">
          <label class="page-size-label">Show:</label>
          <v-select
            v-model="perPage"
            :options="pageSizeOptions"
            :reduce="(option) => option.value"
            label="label"
            :clearable="false"
            :searchable="false"
            append-to-body
            class="page-size-select-compact"
            @input="onPageSizeChange"
          />
        </div>

        <div class="pagination-count-display">
          <span class="count-text">
            Showing <strong>{{ displayedRange.start }}</strong> to
            <strong>{{ displayedRange.end }}</strong> of
            <strong>{{ totalCount }}</strong>
          </span>
        </div>

        <div class="pagination-wrapper-compact">
          <b-pagination
            v-model="currentPage"
            :total-rows="totalCount"
            :per-page="perPage"
            size="sm"
            align="center"
            class="pagination-compact mb-0"
            @input="loadExitInterviews"
          />
        </div>
      </div>

      <b-row class="align-items-center filter-row mb-2">
        <b-col cols="12" class="filter-toolbar d-flex align-items-center">
          <div
            class="filter-fields-group filter-fields-group--cols-3 d-flex align-items-center"
          >
            <b-form-input
              v-model="searchQuery"
              class="filter-search-input"
              placeholder="Search by staff, reason, interviewer..."
            />
            <v-select
              v-model="selectedDepartment"
              :options="departments"
              :clearable="true"
              label="name"
              :reduce="(val) => val.id"
              placeholder="Department"
              class="filter-control filter-field-item"
              @input="onDepartmentChange"
            />
          </div>

          <div class="filter-actions-group d-flex align-items-center ml-auto">
            <b-button
              v-if="rights.add"
              variant="gradient-primary"
              class="action-btn-filter add-sheet-btn text-nowrap"
              @click="openAddModal"
            >
              <feather-icon icon="PlusIcon" class="mr-50" />
              <span>Add Exit Interview</span>
            </b-button>
          </div>
        </b-col>
      </b-row>

      <div class="table-scroll-container">
        <b-table
          class="planner-list-table mt-1"
          :items="filteredExitInterviews"
          :fields="fields"
          :busy="dataLoading"
          :responsive="false"
          hover
          small
          show-empty
        >
          <template #empty>
            <h3 v-if="!dataLoading" class="mt-1 mb-1 text-center">
              No exit interviews found
            </h3>
          </template>

          <template #table-busy>
            <div class="list-skeleton">
              <div
                v-for="n in 6"
                :key="'skeleton-' + n"
                class="list-skeleton__row"
                :style="{
                  gridTemplateColumns: `repeat(${fields.length}, minmax(0, 1fr))`,
                }"
              >
                <div
                  v-for="(field, index) in fields"
                  :key="'skeleton-cell-' + n + '-' + index"
                  class="list-skeleton__cell"
                >
                  <div
                    class="list-skeleton__bar"
                    :style="{ width: skeletonBarWidth(index) }"
                  ></div>
                </div>
              </div>
            </div>
          </template>

          <template #cell(index)="{ index }">
            {{ (currentPage - 1) * perPage + index + 1 }}
          </template>

        <template #cell(departmentName)="{ item }">
          <b-badge variant="light-info">
            {{ item.departmentName || "N/A" }}
          </b-badge>
        </template>

        <template #cell(staffName)="{ item }">
          <b-badge variant="light-primary">
            {{ item.staffName }}
          </b-badge>
        </template>

        <template #cell(leavingDate)="{ item }">
          <b-badge variant="light-info">
            {{ formatDate(item.leavingDate) }}
          </b-badge>
        </template>

        <template #cell(reason)="{ item }">
          <span
            class="text-truncate d-inline-block"
            style="max-width: 200px"
            :title="item.reason"
          >
            {{ item.reason }}
          </span>
        </template>

        <template #cell(interviewBy)="{ item }">
          <b-badge variant="light-secondary">
            {{ item.interviewBy }}
          </b-badge>
        </template>

        <template #cell(interviewDate)="{ item }">
          <b-badge variant="light-success">
            {{ formatDate(item.interviewDate) }}
          </b-badge>
        </template>

        <template #cell(attachment)="{ item }">
          <b-badge
            v-if="attachmentCount(item) > 0"
            variant="light-primary"
            class="cursor-pointer"
            @click="viewExitInterview(item)"
          >
            <feather-icon icon="PaperclipIcon" size="12" class="mr-25" />
            {{ attachmentCount(item) }}
          </b-badge>
          <span v-else class="text-muted">—</span>
        </template>

        <template #head(actions)="data">
          <div class="text-center">
            <span>{{ data.label }}</span>
          </div>
        </template>

        <template #cell(actions)="{ item }">
          <div class="d-flex justify-content-center">
            <b-button
              variant="gradient-primary"
              v-b-tooltip.hover.top
              title="View"
              class="action-btn-table mr-1"
              @click="viewExitInterview(item)"
            >
              <feather-icon icon="EyeIcon" />
            </b-button>

            <b-button
              v-if="rights.edit"
              variant="gradient-primary"
              v-b-tooltip.hover.top
              title="Edit"
              class="action-btn-table mr-1"
              @click="editExitInterview(item)"
            >
              <feather-icon icon="EditIcon" />
            </b-button>

            <b-button
              v-if="rights.delete"
              variant="outline-danger"
              v-b-tooltip.hover.top
              title="Delete"
              class="action-btn-outline delete-btn"
              @click="deleteExitInterview(item)"
            >
              <feather-icon icon="Trash2Icon" />
            </b-button>
          </div>
        </template>
        </b-table>
      </div>
    </b-card>

    <!-- Add/Edit Modal -->
    <b-modal
      v-model="showModal"
      :title="isEdit ? 'Edit Exit Interview' : 'Add Exit Interview'"
      @hidden="resetForm"
      centered
      no-close-on-backdrop
      size="lg"
      modal-class="exit-interview-modal"
    >
      <b-form class="exit-interview-form" @submit.prevent>
        <b-row>
          <b-col md="6">
            <b-form-group label="Department" label-for="department">
              <v-select
                v-model="modalSelectedDepartment"
                :options="departments"
                class="filter-control filter-field-item w-100"
                :clearable="true"
                label="name"
                :reduce="(val) => val.id"
                placeholder="Select department"
                @input="onModalDepartmentChange"
              />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Staff *" label-for="staff">
              <v-select
                v-model="form.staffID"
                :options="modalFilteredStaff"
                :clearable="false"
                label="name"
                :reduce="(val) => val.id"
                placeholder="Select staff member"
                class="filter-control filter-field-item w-100"
              />
            </b-form-group>
          </b-col>
        </b-row>

        <b-row>
          <b-col md="6">
            <b-form-group label="Leaving Date *" label-for="leavingDate">
              <flat-pickr
                :config="dateConfig"
                v-model="form.leavingDate"
                class="form-control filter-date-item"
                placeholder="Select leaving date"
              />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Interview Date *" label-for="interviewDate">
              <flat-pickr
                :config="dateConfig"
                v-model="form.interviewDate"
                class="form-control filter-date-item"
                placeholder="Select interview date"
              />
            </b-form-group>
          </b-col>
        </b-row>

        <b-row>
          <b-col md="12">
            <b-form-group label="Reason for Leaving *" label-for="reason">
              <b-form-textarea
                v-model.trim="form.reason"
                placeholder="Enter reason for leaving"
                rows="3"
              />
            </b-form-group>
          </b-col>
        </b-row>

        <b-row>
          <b-col md="12">
            <b-form-group label="Interview By *" label-for="interviewBy">
              <b-form-input
                v-model.trim="form.interviewBy"
                placeholder="Enter interviewer name"
              />
            </b-form-group>
          </b-col>
        </b-row>

        <b-row>
          <b-col md="12">
            <b-form-group label="Interview Notes" label-for="notes">
              <b-form-textarea
                v-model.trim="form.notes"
                placeholder="Enter interview notes and feedback"
                rows="4"
              />
            </b-form-group>
          </b-col>
        </b-row>

        <b-row>
          <b-col md="12">
            <b-form-group label="Attachments">
              <div class="exit-attachment-grid">
                <div
                  class="exit-attachment-tile exit-attachment-tile--add"
                  @click="
                    !attachmentUploading && $refs.exitInterviewAttachment.click()
                  "
                >
                  <b-spinner
                    v-if="attachmentUploading"
                    small
                    variant="primary"
                  />
                  <feather-icon
                    v-else
                    icon="PlusIcon"
                    size="32"
                    class="text-primary"
                  />
                  <span class="exit-attachment-label">Add files</span>
                </div>
                <div
                  v-for="tile in attachmentTiles"
                  :key="tile.key"
                  class="exit-attachment-tile"
                  :class="{
                    'exit-attachment-tile--preview': tile.isImage,
                    'exit-attachment-tile--pending': tile.pending,
                  }"
                  @click="!tile.pending && openAttachment(tile.url)"
                >
                  <b-avatar
                    v-if="!tile.pending"
                    size="22"
                    variant="primary"
                    class="exit-attachment-remove"
                    @click.stop="removeAttachment(tile.index)"
                  >
                    <feather-icon icon="XIcon" size="14" />
                  </b-avatar>
                  <img
                    v-if="tile.isImage"
                    :src="tile.previewUrl"
                    :alt="tile.label"
                    class="exit-attachment-preview"
                    @error="onAttachmentPreviewError($event, tile.url)"
                  />
                  <feather-icon
                    v-else
                    icon="FileTextIcon"
                    size="28"
                    class="text-primary"
                  />
                  <span
                    class="exit-attachment-label text-truncate"
                    :title="tile.label"
                  >
                    {{ tile.label }}
                  </span>
                </div>
                <input
                  ref="exitInterviewAttachment"
                  type="file"
                  hidden
                  multiple
                  accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
                  @change="onAttachmentFilesSelected"
                />
              </div>
              <small class="text-muted d-block mt-50">
                Max {{ attachmentMaxSizeLabel }} per file. Multiple files allowed;
                images show preview.
              </small>
            </b-form-group>
          </b-col>
        </b-row>
      </b-form>

      <template #modal-footer>
        <b-button variant="outline-secondary" @click="showModal = false">
          Cancel
        </b-button>
        <b-button
          variant="primary"
          @click="saveExitInterview"
          :disabled="loading"
        >
          <b-spinner v-if="loading" small />
          {{ isEdit ? "Update" : "Save" }}
        </b-button>
      </template>
    </b-modal>

    <!-- View Modal -->
    <b-modal
      v-model="showViewModal"
      title="Exit Interview Details"
      centered
      size="lg"
      header-bg-variant="light"
      header-text-variant="dark"
    >
      <div v-if="selectedInterview" class="view-modal-content">
        <div class="info-section">
          <h6 class="section-title">
            <feather-icon icon="UserIcon" class="mr-1" />
            Staff Information
          </h6>
          <div class="info-grid">
            <div class="info-item">
              <label>Staff Name</label>
              <div class="info-value">
                {{ selectedInterview.staffName || "N/A" }}
              </div>
            </div>
            <div class="info-item">
              <label>Department</label>
              <div class="info-value">
                {{ selectedInterview.departmentName || "N/A" }}
              </div>
            </div>
            <div v-if="selectedInterview.addedByName || selectedInterview.userName" class="info-item">
              <label>Recorded By</label>
              <div class="info-value">
                {{
                  selectedInterview.addedByName ||
                  selectedInterview.userName ||
                  "—"
                }}
              </div>
            </div>
          </div>
        </div>

        <div class="info-section">
          <h6 class="section-title">
            <feather-icon icon="CalendarIcon" class="mr-1" />
            Interview Dates
          </h6>
          <div class="info-grid">
            <div class="info-item">
              <label>Leaving Date</label>
              <div class="info-value">
                {{ formatDate(selectedInterview.leavingDate) }}
              </div>
            </div>
            <div class="info-item">
              <label>Interview Date</label>
              <div class="info-value">
                {{ formatDate(selectedInterview.interviewDate) }}
              </div>
            </div>
          </div>
        </div>

        <div class="info-section">
          <h6 class="section-title">
            <feather-icon icon="FileTextIcon" class="mr-1" />
            Interview Details
          </h6>
          <div class="info-item full-width">
            <label>Reason for Leaving</label>
            <div class="info-value reason-text">
              {{ selectedInterview.reason || "N/A" }}
            </div>
          </div>
          <div class="info-item full-width">
            <label>Interviewed By</label>
            <div class="info-value">
              {{ selectedInterview.interviewBy || "N/A" }}
            </div>
          </div>
          <div v-if="selectedInterview.notes" class="info-item full-width">
            <label>Interview Notes</label>
            <div class="info-value notes-text">
              {{ selectedInterview.notes }}
            </div>
          </div>
          <div
            v-if="viewAttachmentDocs.length"
            class="info-item full-width"
          >
            <label>Attachments</label>
            <div class="attachment-view-list">
              <div
                v-for="(doc, index) in viewAttachmentDocs"
                :key="`${doc.url}-${index}`"
                class="attachment-view-item"
                @click="openAttachment(doc.url)"
              >
                <img
                  v-if="docIsImage(doc)"
                  :src="docPreviewUrl(doc)"
                  :alt="docLabel(doc, index)"
                  class="attachment-view-thumb"
                  @error="onAttachmentPreviewError($event, doc.url)"
                />
                <div v-else class="attachment-view-doc">
                  <feather-icon icon="FileTextIcon" size="22" class="text-primary" />
                </div>
                <span class="attachment-view-name text-truncate">{{
                  docLabel(doc, index)
                }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <template #modal-footer>
        <b-button variant="outline-secondary" @click="showViewModal = false">
          <feather-icon icon="XIcon" class="mr-1" />
          Close
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BInputGroup,
  BInputGroupPrepend,
  BFormInput,
  BFormTextarea,
  BButton,
  BTable,
  BPagination,
  BModal,
  BForm,
  BFormGroup,
  BSpinner,
  BBadge,
  BAvatar,
  VBTooltip,
} from "bootstrap-vue";
import vSelect from "vue-select";
import flatPickr from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";
import { mapActions } from "vuex";
import {
  uploadToMySkool,
  buildUploadedAttachment,
  parseDocUrls,
  getMediaPreviewUrl,
  isImageUrl,
  isImageMediaType,
  handleDrivePreviewError,
  getDocumentDisplayName,
} from "@/utils/gdriveUpload";

const ATTACHMENT_MAX_BYTES = 25 * 1024 * 1024;

export default {
  name: "ExitInterview",
  components: {
    BCard,
    BRow,
    BCol,
    BInputGroup,
    BInputGroupPrepend,
    BFormInput,
    BFormTextarea,
    BButton,
    BTable,
    BPagination,
    BModal,
    BForm,
    BFormGroup,
    BSpinner,
    BBadge,
    BAvatar,
    vSelect,
    flatPickr,
  },
  directives: {
    "b-tooltip": VBTooltip,
  },
  data() {
    return {
      rights: {},
      exitInterviews: [],
      allStaff: [],
      filteredStaff: [],
      modalFilteredStaff: [],
      departments: [],
      selectedDepartment: null,
      modalSelectedDepartment: null,
      searchQuery: "",
      showModal: false,
      showViewModal: false,
      selectedInterview: null,
      isEdit: false,
      loading: false,
      dataLoading: false,
      attachmentUploading: false,
      attachDocs: [],
      pendingLocalPreviews: [],
      currentPage: 1,
      perPage: 10,
      totalCount: 0,
      pageSizeOptions: [
        { label: "10", value: 10 },
        { label: "20", value: 20 },
        { label: "50", value: 50 },
        { label: "100", value: 100 },
      ],
      dateConfig: {
        altFormat: "j M, Y",
        altInput: true,
        dateFormat: "Y-m-d",
      },
      fields: [
        {
          label: "S.No",
          key: "index",
          thClass: "text-center",
          tdClass: "text-center",
        },
        { key: "departmentName", label: "Department" },
        { key: "staffName", label: "Staff Name" },
        { key: "leavingDate", label: "Leaving Date" },
        { key: "reason", label: "Reason" },
        { key: "interviewBy", label: "Interview By" },
        { key: "interviewDate", label: "Interview Date" },
        { key: "attachment", label: "Files", thClass: "text-center", tdClass: "text-center" },
        {
          key: "actions",
          label: "Actions",
          thClass: "text-center",
          tdClass: "text-center",
        },
      ],
      form: {
        id: 0,
        staffID: null,
        leavingDate: null,
        interviewDate: null,
        reason: "",
        interviewBy: "",
        notes: "",
        attachment: "",
        userID: 0,
        addedBy: 0,
        updatedBy: 0,
        campusID: this.$store.state.userData.cId,
      },
    };
  },
  computed: {
    attachmentMaxSizeLabel() {
      return "25 MB";
    },
    viewAttachmentDocs() {
      if (!this.selectedInterview) return [];
      const raw =
        this.selectedInterview.attachment ||
        this.selectedInterview.attachments ||
        "";
      return parseDocUrls(raw);
    },
    attachmentTiles() {
      const tiles = [];

      this.pendingLocalPreviews.forEach((item, i) => {
        tiles.push({
          key: `pending-${i}-${item.blobUrl}`,
          url: item.blobUrl,
          previewUrl: item.blobUrl,
          isImage: true,
          pending: true,
          index: i,
          label: item.name,
        });
      });

      this.attachDocs.forEach((doc, index) => {
        const isImage = this.docIsImage(doc);
        tiles.push({
          key: `doc-${index}-${doc.url}`,
          url: doc.url,
          previewUrl: this.docPreviewUrl(doc),
          isImage,
          pending: false,
          index,
          label: this.docLabel(doc, index),
        });
      });

      return tiles;
    },
    displayedRange() {
      if (this.totalCount === 0) {
        return { start: 0, end: 0 };
      }
      const start = (this.currentPage - 1) * this.perPage + 1;
      const end = Math.min(this.currentPage * this.perPage, this.totalCount);
      return { start, end };
    },
    filteredExitInterviews() {
      if (!this.searchQuery) return this.exitInterviews;
      const q = this.searchQuery.toLowerCase();
      return this.exitInterviews.filter((interview) => {
        const staffName = (interview.staffName || "").toLowerCase();
        const reason = (interview.reason || "").toLowerCase();
        const interviewBy = (interview.interviewBy || "").toLowerCase();
        return (
          staffName.includes(q) ||
          reason.includes(q) ||
          interviewBy.includes(q)
        );
      });
    },
  },
  async created() {
    // Initialize rights from menu and enforce view permission
    let right = {};
    this.$store.state.menu.forEach((el) => {
      el.children.forEach((ch) => {
        right[ch.route] = {
          view: ch.view,
          add: ch.add,
          edit: ch.edit,
          delete: ch.delete,
          assignSections: ch.assignSections,
        };
      });
    });
    this.$store.commit("setRights", right);

    if (!this.$store.state.rights[this.$route.name]) {
      this.$router.replace({ name: "misc-not-authorized" });
      return;
    }
    this.rights = this.$store.state.rights[this.$route.name];
    if (!this.rights.view) {
      this.$router.replace({ name: "misc-not-authorized" });
      return;
    }

    // Load departments first, then exit interviews
    await this.loadDepartments();
    this.loadExitInterviews();
    this.loadStaffByDepartment(); // Load all staff initially

    // Fallback test data if API fails
    setTimeout(() => {
      if (this.departments.length === 0) {
        console.log("No departments loaded, using fallback data");
        this.departments = [
          { id: 25, name: "Administration" },
          { id: 26, name: "Helping Staff" },
          { id: 27, name: "Accounts" },
          { id: 28, name: "Teaching Staff" },
          { id: 36, name: "Management" },
          { id: 38, name: "HR" },
        ];
      }
    }, 3000);
  },
  beforeDestroy() {
    this.clearPendingPreviews();
  },
  methods: {
    ...mapActions({ get: "get", post: "post", put: "put", delete: "delete" }),

    resolveUserID() {
      return (
        Number(this.$store.state.userData.userID) ||
        Number(this.$store.state.userData.id) ||
        0
      );
    },

    skeletonBarWidth(index) {
      const widths = ["42%", "72%", "58%", "65%", "80%", "70%", "48%", "40%"];
      return widths[index % widths.length];
    },

    onPageSizeChange() {
      this.currentPage = 1;
      this.loadExitInterviews();
    },

    attachmentCount(item) {
      if (!item) return 0;
      const raw = item.attachment || item.attachments || "";
      return this.parseAttachmentString(raw).length;
    },

    async loadDepartments() {
      try {
        const obj = {
          url:
            this.$store.state.domain +
            "StaffDepartments?db=" +
            this.$store.state.userData.db +
            "&cId=" +
            this.$store.state.userData.cId,
          token: this.$store.state.userData.token,
        };

        console.log("Loading departments with URL:", obj.url); // Debug log
        const response = await this.get(obj);
        console.log("Departments response:", response); // Debug log

        // Handle different response structures
        if (response) {
          if (response.data && Array.isArray(response.data)) {
            this.departments = response.data;
            console.log("Using response.data:", this.departments);
          } else if (Array.isArray(response)) {
            this.departments = response;
            console.log("Using direct response:", this.departments);
          } else {
            console.warn("Unexpected response structure:", response);
            this.departments = [];
          }
        } else {
          console.warn("No response received");
          this.departments = [];
        }

        console.log("Final departments loaded:", this.departments); // Debug log
      } catch (error) {
        console.error("Error loading departments:", error);
        this.$bvToast.toast("Error loading departments!", {
          title: this.$t("Error!"),
          variant: "danger",
          solid: true,
        });
        this.departments = [];
      }
    },

    onDepartmentChange() {
      this.loadStaffByDepartment();
      this.loadExitInterviews(); // Reload exit interviews with department filter
    },

    onModalDepartmentChange() {
      this.loadModalStaffByDepartment();
    },

    async loadStaffByDepartment() {
      try {
        let url;

        if (!this.selectedDepartment) {
          // Load all staff when no department is selected
          url =
            this.$store.state.domain +
            "staff?db=" +
            this.$store.state.userData.db +
            "&cID=" +
            this.$store.state.userData.cId;
        } else {
          // Load staff by department
          url =
            this.$store.state.domain +
            "staff/LoadDataPagination?db=" +
            this.$store.state.userData.db +
            "&cId=" +
            this.$store.state.userData.cId +
            "&dptID=" +
            this.selectedDepartment +
            "&status=present&page=1&appointedFrom=&ap";
        }

        const obj = {
          url: url,
          token: this.$store.state.userData.token,
        };

        console.log("Loading staff with URL:", url); // Debug log
        const response = await this.get(obj);
        console.log("Staff response:", response); // Debug log

        if (response) {
          let staffData = [];

          // Handle different response structures
          if (response.data && Array.isArray(response.data)) {
            staffData = response.data;
          } else if (Array.isArray(response)) {
            staffData = response;
          }

          // Filter active staff and ensure they have names
          this.filteredStaff = staffData.filter(
            (staff) =>
              staff.isActive !== false && staff.name && staff.name.trim() !== ""
          );

          console.log("Filtered staff loaded:", this.filteredStaff); // Debug log
        } else {
          this.filteredStaff = [];
        }

        // Reset staff selection when department changes
        this.form.staffID = null;
      } catch (error) {
        console.error("Error loading staff:", error);
        this.$bvToast.toast("Error loading staff data!", {
          title: this.$t("Error!"),
          variant: "danger",
          solid: true,
        });
        this.filteredStaff = [];
      }
    },

    async loadModalStaffByDepartment() {
      try {
        let url;

        if (!this.modalSelectedDepartment) {
          // Load all staff when no department is selected
          url =
            this.$store.state.domain +
            "staff?db=" +
            this.$store.state.userData.db +
            "&cID=" +
            this.$store.state.userData.cId;
        } else {
          // Load staff by department
          url =
            this.$store.state.domain +
            "staff/LoadDataPagination?db=" +
            this.$store.state.userData.db +
            "&cId=" +
            this.$store.state.userData.cId +
            "&dptID=" +
            this.modalSelectedDepartment +
            "&status=present&page=1&appointedFrom=&ap";
        }

        const obj = {
          url: url,
          token: this.$store.state.userData.token,
        };

        console.log("Loading modal staff with URL:", url); // Debug log
        const response = await this.get(obj);
        console.log("Modal staff response:", response); // Debug log

        if (response) {
          let staffData = [];

          // Handle different response structures
          if (response.data && Array.isArray(response.data)) {
            staffData = response.data;
          } else if (Array.isArray(response)) {
            staffData = response;
          }

          // Filter active staff and ensure they have names
          this.modalFilteredStaff = staffData.filter(
            (staff) =>
              staff.isActive !== false && staff.name && staff.name.trim() !== ""
          );

          console.log("Modal filtered staff loaded:", this.modalFilteredStaff); // Debug log
        } else {
          this.modalFilteredStaff = [];
        }

        // Reset staff selection when department changes
        this.form.staffID = null;
      } catch (error) {
        console.error("Error loading modal staff:", error);
        this.$bvToast.toast("Error loading staff data!", {
          title: this.$t("Error!"),
          variant: "danger",
          solid: true,
        });
        this.modalFilteredStaff = [];
      }
    },

    async loadExitInterviews() {
      this.dataLoading = true;
      try {
        let url =
          this.$store.state.domain +
          "ExitInterview/GetByPagination?db=" +
          this.$store.state.userData.db +
          "&cID=" +
          this.$store.state.userData.cId +
          "&page=" +
          this.currentPage +
          "&pageSize=" +
          this.perPage;

        // Add department filter if selected
        if (this.selectedDepartment) {
          url += "&departmentID=" + this.selectedDepartment;
        }
        const userID = this.resolveUserID();
        if (userID) {
          url += "&userID=" + userID;
        }

        const obj = {
          url: url,
          token: this.$store.state.userData.token,
          dashboard: true,
        };

        console.log("Loading exit interviews with URL:", url); // Debug log
        let res = await this.get(obj);
        console.log("Exit interviews response:", res); // Debug log

        if (res && Array.isArray(res.data)) {
          // Map department names to the data
          this.exitInterviews = res.data.map((interview) => {
            // Find department name from departments array
            const department = this.departments.find(
              (dept) => dept.id === interview.departmentID
            );
            return {
              ...interview,
              departmentName: department ? department.name : "N/A",
            };
          });
          this.totalCount = res.total || res.data.length;
        } else {
          this.exitInterviews = [];
          this.totalCount = 0;
        }
      } catch (error) {
        console.error("Error loading exit interviews:", error);
        this.$bvToast.toast("Error loading exit interviews!", {
          title: this.$t("Error!"),
          variant: "danger",
          solid: true,
        });
        this.exitInterviews = [];
        this.totalCount = 0;
      }
      this.dataLoading = false;
    },

    async openAddModal() {
      this.isEdit = false;
      this.modalSelectedDepartment = null;
      this.modalFilteredStaff = [];
      this.clearPendingPreviews();
      this.attachDocs = [];
      this.form.attachment = "";
      await this.loadModalStaffByDepartment(); // Load all staff initially
      this.showModal = true;
    },

    async viewExitInterview(item) {
      try {
        let url =
          this.$store.state.domain +
          "ExitInterview/LoadSelectedData?db=" +
          this.$store.state.userData.db +
          "&id=" +
          item.id +
          "&cID=" +
          this.$store.state.userData.cId;
        const viewUserID = this.resolveUserID();
        if (viewUserID) {
          url += "&userID=" + viewUserID;
        }

        const response = await this.get({
          url,
          token: this.$store.state.userData.token,
        });
        if (response) {
          this.selectedInterview = response;
          this.showViewModal = true;
        } else {
          // Fallback to using the item data if API fails
          this.selectedInterview = item;
          this.showViewModal = true;
        }
      } catch (error) {
        console.error("Error loading exit interview details:", error);
        // Fallback to using the item data if API fails
        this.selectedInterview = item;
        this.showViewModal = true;
      }
    },

    async editExitInterview(item) {
      this.isEdit = true;
      this.form = {
        ...item,
        leavingDate: item.leavingDate
          ? new Date(item.leavingDate).toISOString().split("T")[0]
          : null,
        interviewDate: item.interviewDate
          ? new Date(item.interviewDate).toISOString().split("T")[0]
          : null,
      };

      // Set department and load staff for editing
      if (item.departmentID) {
        this.modalSelectedDepartment = item.departmentID;
        await this.loadModalStaffByDepartment();
      } else {
        this.modalSelectedDepartment = null;
        await this.loadModalStaffByDepartment(); // Load all staff
      }

      // Ensure staff is selected if we have staffID
      if (item.staffID && this.modalFilteredStaff.length > 0) {
        // Find the staff member in the filtered list
        const staffMember = this.modalFilteredStaff.find(
          (staff) => staff.id === item.staffID
        );
        if (staffMember) {
          this.form.staffID = item.staffID;
        }
      }

      const attachmentRaw = item.attachment || item.attachments || "";
      this.attachDocs = parseDocUrls(attachmentRaw);
      this.syncAttachmentField();

      this.showModal = true;
    },

    async deleteExitInterview(item) {
      const result = await Swal.fire({
        title: this.$t("Are you sure?"),
        text: this.$t("You won't be able to revert this!"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, delete it!",
      });

      if (result.isConfirmed) {
        try {
          const obj = {
            url:
              this.$store.state.domain +
              "ExitInterview/Delete?db=" +
              this.$store.state.userData.db +
              "&id=" +
              item.id +
              "&cID=" +
              this.$store.state.userData.cId +
              "&userID=" +
              this.resolveUserID(),
            message: "Exit interview deleted successfully.",
            context: this,
            token: this.$store.state.userData.token,
          };
          const success = await this.delete(obj);
          if (success) {
            this.loadExitInterviews();
          }
        } catch (error) {
          console.error("Error deleting exit interview:", error);
          this.$bvToast.toast("Error deleting exit interview!", {
            title: this.$t("Error!"),
            variant: "danger",
            solid: true,
          });
        }
      }
    },

    async saveExitInterview() {
      // Validation
      if (
        !this.form.staffID ||
        !this.form.leavingDate ||
        !this.form.interviewDate ||
        !this.form.reason.trim() ||
        !this.form.interviewBy.trim()
      ) {
        this.$bvToast.toast("Please fill all required fields!", {
          title: this.$t("Error!"),
          variant: "danger",
          solid: true,
        });
        return;
      }

      this.loading = true;

      try {
        // Set timestamps and department (if selected)
        if (this.modalSelectedDepartment) {
          this.form.departmentID = this.modalSelectedDepartment;
        }

        const userID = this.resolveUserID();
        this.form.userID = userID;
        if (this.isEdit) {
          this.form.updatedBy = userID;
          this.form.updatedOn = new Date().toISOString();
        } else {
          this.form.addedBy = userID;
          this.form.addedOn = new Date().toISOString();
        }

        this.syncAttachmentField();

        const obj = {
          url:
            this.$store.state.domain +
            "ExitInterview/Save?db=" +
            this.$store.state.userData.db +
            "&cID=" +
            this.$store.state.userData.cId +
            "&userID=" +
            userID,
          body: this.form,
          message:
            "Exit interview " +
            (this.isEdit ? "updated" : "created") +
            " successfully.",
          context: this,
          token: this.$store.state.userData.token,
        };

        const success = await this.post(obj);

        if (success) {
          this.showModal = false;
          this.loadExitInterviews();
        }
      } catch (error) {
        console.error("Error saving exit interview:", error);
        this.$bvToast.toast("Error saving exit interview!", {
          title: this.$t("Error!"),
          variant: "danger",
          solid: true,
        });
      }

      this.loading = false;
    },

    resetForm() {
      this.form = {
        id: 0,
        staffID: null,
        leavingDate: null,
        interviewDate: null,
        reason: "",
        interviewBy: "",
        notes: "",
        attachment: "",
        userID: 0,
        addedBy: 0,
        updatedBy: 0,
        campusID: this.$store.state.userData.cId,
      };
      this.clearPendingPreviews();
      this.attachDocs = [];
      this.attachmentUploading = false;
      this.modalSelectedDepartment = null;
      this.filteredStaff = [];
      this.modalFilteredStaff = [];
    },

    parseAttachmentString(value) {
      if (!value) return [];
      return String(value)
        .split(",")
        .map((part) => part.trim())
        .filter(Boolean);
    },

    syncAttachmentField() {
      this.form.attachment = this.attachDocs
        .map((doc) => doc.url)
        .filter(Boolean)
        .join(",");
    },

    formatFileSize(bytes) {
      if (!bytes && bytes !== 0) return "";
      const mb = bytes / (1024 * 1024);
      if (mb >= 0.1) return `${mb.toFixed(1)} MB`;
      const kb = bytes / 1024;
      return `${Math.max(1, Math.round(kb))} KB`;
    },

    clearPendingPreviews() {
      this.pendingLocalPreviews.forEach((item) => {
        if (item.blobUrl) URL.revokeObjectURL(item.blobUrl);
      });
      this.pendingLocalPreviews = [];
    },

    docIsImage(doc) {
      if (!doc) return false;
      return (
        isImageMediaType(doc.type) ||
        isImageUrl(doc.url, doc.type) ||
        /\.(jpg|jpeg|png|webp|avif|gif|svg|bmp)$/i.test(String(doc.url || ""))
      );
    },

    docPreviewUrl(doc) {
      if (!doc) return "";
      if (doc.previewUrl) return doc.previewUrl;
      return getMediaPreviewUrl(doc.url, doc.type || "Image");
    },

    docLabel(doc, index) {
      return getDocumentDisplayName(doc.url, index);
    },

    onAttachmentPreviewError(event, url) {
      handleDrivePreviewError(event, url, "Image");
    },

    async onAttachmentFilesSelected() {
      const input = this.$refs.exitInterviewAttachment;
      if (!input || !input.files || !input.files.length) return;

      const files = Array.from(input.files);
      const accepted = [];
      const oversized = [];

      files.forEach((file) => {
        if (file.size > ATTACHMENT_MAX_BYTES) {
          oversized.push(
            `${file.name} (${this.formatFileSize(file.size)})`
          );
        } else {
          accepted.push(file);
        }
      });

      if (oversized.length) {
        this.$bvToast.toast(
          `Each file must be ${this.attachmentMaxSizeLabel} or less. Too large: ${oversized.join(", ")}`,
          {
            title: "File size limit",
            variant: "danger",
            solid: true,
          }
        );
      }

      if (!accepted.length) {
        input.value = "";
        return;
      }

      this.clearPendingPreviews();
      accepted.forEach((file) => {
        if (file.type && file.type.startsWith("image/")) {
          this.pendingLocalPreviews.push({
            name: file.name,
            blobUrl: URL.createObjectURL(file),
          });
        }
      });

      this.attachmentUploading = true;
      try {
        const uploaded = await uploadToMySkool(accepted);
        (uploaded || []).forEach((item) => {
          const doc = buildUploadedAttachment(item);
          if (
            doc.url &&
            !this.attachDocs.some((existing) => existing.url === doc.url)
          ) {
            this.attachDocs.push(doc);
          }
        });
        this.syncAttachmentField();
      } catch (error) {
        console.error("Exit interview attachment upload failed:", error);
        this.$bvToast.toast("Failed to upload attachment(s).", {
          title: this.$t("Error!"),
          variant: "danger",
          solid: true,
        });
      } finally {
        this.clearPendingPreviews();
        this.attachmentUploading = false;
        input.value = "";
      }
    },

    removeAttachment(index) {
      this.attachDocs.splice(index, 1);
      this.syncAttachmentField();
    },

    openAttachment(url) {
      if (!url) return;
      window.open(url, "_blank");
    },

    isImageAttachment(url) {
      if (!url) return false;
      return (
        isImageUrl(url, "Image") ||
        /\.(jpg|jpeg|png|webp|avif|gif|svg|bmp)$/i.test(String(url))
      );
    },

    attachmentFileName(url) {
      return getDocumentDisplayName(url);
    },

    formatDate(date) {
      if (!date) return "N/A";
      return new Date(date).toLocaleDateString("en-UK", {
        year: "numeric",
        day: "numeric",
        month: "short",
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../planner/plannerListTable.scss";
@import "@core/scss/vue/libs/vue-select.scss";
@import "@core/scss/vue/libs/vue-flatpicker.scss";

.exit-interview-form {
  ::v-deep .filter-date-item,
  ::v-deep input.flatpickr-input {
    height: 46px !important;
    min-height: 46px !important;
    border-radius: 12px !important;
    border: 1.5px solid #e0d8ff !important;
  }
}

.exit-attachment-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.exit-attachment-tile {
  position: relative;
  width: 110px;
  height: 96px;
  padding: 0.35rem;
  border: 1.5px dashed #e0d8ff;
  border-radius: 12px;
  background: linear-gradient(180deg, #ffffff 0%, #faf9ff 100%);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  text-align: center;
  transition: all 0.2s ease;

  &:hover {
    border-color: #7367f0;
    box-shadow: 0 4px 12px rgba(115, 103, 240, 0.12);
  }

  &--add {
    background: #faf9ff;
    height: 96px;
  }

  &--preview {
    min-height: 96px;
    height: auto;
    padding: 6px;
  }

  &--pending {
    opacity: 0.88;
    pointer-events: none;
  }
}

.exit-attachment-preview {
  width: 100%;
  height: 52px;
  object-fit: cover;
  border-radius: 8px;
  border: 1px solid #e0d8ff;
  background: #fff;
}

.exit-attachment-label {
  font-size: 0.65rem;
  margin-top: 0.25rem;
  max-width: 100%;
  color: #5e5873;
  font-weight: 500;
}

.exit-attachment-remove {
  position: absolute;
  top: -6px;
  right: -6px;
  cursor: pointer;
  z-index: 1;
  box-shadow: 0 2px 8px rgba(115, 103, 240, 0.25);
}

.cursor-pointer {
  cursor: pointer;
}

.view-modal-content {
  .info-section {
    margin-bottom: 2rem;

    .section-title {
      color: #2c3e50;
      font-weight: 600;
      font-size: 0.875rem;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      margin-bottom: 1rem;
      padding-bottom: 0.5rem;
      border-bottom: 2px solid #e3ebf0;
      display: flex;
      align-items: center;

      .feather {
        color: #2c3e50;
      }
    }

    .info-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 1.5rem;

      @media (max-width: 768px) {
        grid-template-columns: 1fr;
        gap: 1rem;
      }
    }

    .info-item {
      &.full-width {
        grid-column: 1 / -1;
      }

      label {
        display: block;
        font-size: 0.75rem;
        font-weight: 600;
        color: #6c757d;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        margin-bottom: 0.5rem;
      }

      .info-value {
        font-size: 0.95rem;
        color: #2c3e50;
        font-weight: 500;
        padding: 0.75rem 1rem;
        background-color: #f8f9fa;
        border: 1px solid #e9ecef;
        border-radius: 0.375rem;
        min-height: 2.5rem;
        display: flex;
        align-items: center;

        &.reason-text,
        &.notes-text {
          white-space: pre-wrap;
          word-wrap: break-word;
          min-height: auto;
          padding: 1rem;
          line-height: 1.5;
        }
      }
    }
  }
}

// Professional modal header styling
:deep(.modal-header) {
  border-bottom: 1px solid #e3ebf0;

  .modal-title {
    font-weight: 600;
    font-size: 1.125rem;
  }
}

// Professional modal body styling
:deep(.modal-body) {
  padding: 2rem;
}

// Professional modal footer styling
:deep(.modal-footer) {
  border-top: 1px solid #e3ebf0;
  padding: 1rem 2rem;
}

.attachment-view-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: linear-gradient(180deg, #faf9ff 0%, #ffffff 100%);
  border: 1px solid #e0d8ff;
  border-radius: 12px;
}
</style>
